import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSponsoredManager = ({ onClose }) => {
  const { sessionToken } = useUnifiedAuth();
  const [sponsored, setSponsored] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [label, setLabel] = useState('Sponsored');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sponsoredRes, providersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/sponsored-providers`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        }),
        fetch(`${BACKEND_URL}/api/healthcare-providers`),
      ]);
      const sponsoredData = await sponsoredRes.json();
      const providersData = await providersRes.json();
      setSponsored(sponsoredData.sponsored || []);
      setAllProviders(providersData.providers || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const sponsoredIds = new Set(sponsored.map((s) => s.provider_id));

  const filteredProviders = allProviders.filter(
    (p) =>
      !sponsoredIds.has(p.id) &&
      (p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAdd = async (providerId) => {
    setAdding(providerId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sponsored-providers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ provider_id: providerId, label }),
      });
      if (res.ok) {
        fetchData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to add');
      }
    } catch (err) {
      console.error('Error adding sponsored:', err);
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (sponsoredId) => {
    if (!window.confirm('Remove this provider from sponsored?')) return;
    setRemoving(sponsoredId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sponsored-providers/${sponsoredId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        setSponsored((prev) => prev.filter((s) => s.id !== sponsoredId));
      }
    } catch (err) {
      console.error('Error removing sponsored:', err);
    } finally {
      setRemoving(null);
    }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-sponsored-manager">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Sponsored Providers</h2>
            <p className="text-sm text-gray-500 mt-0.5">{sponsored.length} active sponsored provider{sponsored.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-sponsored-manager">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              {/* Current Sponsored */}
              <div className="p-5 border-b">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Currently Sponsored</h3>
                {sponsored.length === 0 ? (
                  <p className="text-gray-400 text-sm py-4 text-center">No sponsored providers yet</p>
                ) : (
                  <div className="space-y-2">
                    {sponsored.map((s) => (
                      <div key={s.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="sponsored-item">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{s.provider_name}</span>
                            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full font-medium">{s.label}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{s.category} {s.area ? `· ${s.area}` : ''} · Added {formatDate(s.added_at)}</p>
                        </div>
                        <button
                          onClick={() => handleRemove(s.id)}
                          disabled={removing === s.id}
                          className="ml-3 px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
                          data-testid={`remove-sponsored-${s.id}`}
                        >
                          {removing === s.id ? '...' : 'Remove'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Provider */}
              <div className="p-5">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Add Provider to Sponsored</h3>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search providers..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="search-providers-input"
                  />
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Label"
                    className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="sponsored-label-input"
                  />
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredProviders.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">
                      {searchQuery ? 'No matching providers found' : 'All providers are already sponsored'}
                    </p>
                  ) : (
                    filteredProviders.map((p) => (
                      <div key={p.id} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 hover:border-gray-300 transition-colors" data-testid="available-provider-item">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{p.name}</p>
                          <p className="text-xs text-gray-500">{p.category} {p.area ? `· ${p.area}` : ''}</p>
                        </div>
                        <button
                          onClick={() => handleAdd(p.id)}
                          disabled={adding === p.id}
                          className="ml-3 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 shrink-0"
                          data-testid={`add-sponsored-${p.id}`}
                        >
                          {adding === p.id ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSponsoredManager;
